/**
 * Wait Time Service — Orchestrator
 * Tries providers in priority order with a 3-second total time budget.
 * Returns the first successful provider result, cached with stale-while-revalidate.
 */
import { WaitTimeResult, WaitTimeProvider, FLAGS } from './types';
import { airportSiteProvider } from './providers/airportSiteProvider';
import { estimateProvider } from './providers/estimateProvider';
import { waitTimeCache } from './waitTimeCache';

// Airports whose own website publishes live checkpoint waits we can read
// server-side. Kept in sync with the feed registry in
// supabase/functions/_shared/waitFeeds.ts — never add an airport here unless a
// verified feed exists, or we would label an estimate as live data.
const AIRPORTS_WITH_OFFICIAL_SOURCE = new Set([
  'SFO', 'MSP', 'STL', 'OMA', 'OKC', 'CHS', 'CMH', 'ORD',
  'EWR', 'JFK', 'LGA', 'LAS',
]);

const TOTAL_TIMEOUT_MS = 3000;

const buildProviderOrder = (airportCode: string): WaitTimeProvider[] => {
  const providers: WaitTimeProvider[] = [];

  // Live airport-operated feed first where one exists; otherwise go straight
  // to the clearly-labelled typical-wait estimate.
  if (AIRPORTS_WITH_OFFICIAL_SOURCE.has(airportCode.toUpperCase())) {
    providers.push(airportSiteProvider);
  }

  providers.push(estimateProvider);

  return providers;
};

/**
 * Primary entry point.
 * Non-breaking: returns null on complete failure (UI should show "Unavailable").
 */
export async function getWaitTime(airportCode: string): Promise<WaitTimeResult | null> {
  const code = airportCode.toUpperCase();
  const debug = FLAGS.DEBUG;
  const start = Date.now();

  // Check cache first
  const cached = waitTimeCache.get(code);
  if (cached && !cached.stale) {
    if (debug) console.log(`[waitTimeService] cache HIT (fresh) for ${code}`);
    return cached.data;
  }

  // If we have stale data, return it immediately but revalidate in background
  if (cached?.stale) {
    if (debug) console.log(`[waitTimeService] cache HIT (stale) for ${code}, revalidating`);
    revalidateInBackground(code);
    return cached.data;
  }

  // No cache — fetch synchronously
  return fetchFromProviders(code, debug, start);
}

async function fetchFromProviders(
  code: string,
  debug: boolean,
  start: number,
): Promise<WaitTimeResult | null> {
  const providers = buildProviderOrder(code);

  // Race providers against total timeout
  const timeoutPromise = new Promise<null>((resolve) =>
    setTimeout(() => resolve(null), TOTAL_TIMEOUT_MS)
  );

  for (const provider of providers) {
    if (Date.now() - start > TOTAL_TIMEOUT_MS) {
      if (debug) console.log(`[waitTimeService] total timeout exceeded`);
      break;
    }

    try {
      const result = await Promise.race([
        provider.fetch(code),
        timeoutPromise,
      ]);

      // A genuine zero ("no wait") is valid data, so only null is rejected here.
      if (result && result.waitTimeMinutes !== null && result.waitTimeMinutes >= 0) {
        if (debug) {
          console.log(`[waitTimeService] chosen source=${result.source} conf=${result.confidence} latency=${Date.now() - start}ms`);
        }

        const waitTimeResult: WaitTimeResult = {
          airportCode: code,
          waitTimeMinutes: result.waitTimeMinutes,
          lastUpdatedISO: result.lastUpdatedISO,
          source: result.source,
          confidence: result.confidence,
          checkpointName: result.checkpointName,
          disclaimer: result.disclaimer,
          raw: result.raw,
        };

        waitTimeCache.set(code, waitTimeResult);
        return waitTimeResult;
      }
    } catch (err) {
      if (debug) console.log(`[waitTimeService] provider ${provider.name} threw`, err);
    }
  }

  if (debug) console.log(`[waitTimeService] all providers failed for ${code}, latency=${Date.now() - start}ms`);
  return null;
}

function revalidateInBackground(code: string): void {
  fetchFromProviders(code, FLAGS.DEBUG, Date.now()).catch(() => {
    // Silently fail background revalidation
  });
}
